import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../lib/api';

interface PricingCardProps {
  tier:                 number;
  name:                 string;
  monthlyUsd:           number;
  annualUsd:            number;
  contactLimit:         number | null;
  includedCardsPerYear: number;
  overageCents:         number;
  priceMonthly:         string;
  priceAnnual:          string;
  annual:               boolean;   // from parent BillingToggle
  popular?:             boolean;
}

// One-line "who it's for" per tier — translates abstract contact limits into
// a person the buyer can picture.
const AUDIENCE: Record<number, string> = {
  1: 'For your closest few',
  2: 'For family, friends, and your circle',
  3: 'For everyone in your life',
};

// Feature list per tier — each tier must be visually longer than the one before.
// Always lead with the included-cards line so the yearly allowance is clear, then
// the overage price right under it so there are no surprises.
const FEATURES: Record<number, string[]> = {
  1: [
    '12 folded cards a year, included',
    'Then just $5.99 a card if you send more',
    'Up to 5 people',
    'Real folded cards, mailed in an envelope',
    'Printing and first-class postage included',
    "Birthday, Valentine's, Mother's and Father's Day",
    'Email + push notifications',
  ],
  2: [
    '24 folded cards a year, included',
    'Then just $5.99 a card if you send more',
    'Up to 20 people',
    'Real folded cards, mailed in an envelope',
    'Printing and first-class postage included',
    'Every occasion type, plus 3 custom',
    'SMS, email + push notifications',
    '14-day free trial',
  ],
  3: [
    '48 folded cards a year, included',
    'Then just $5.99 a card if you send more',
    'Unlimited people',
    'Real folded cards, mailed in an envelope',
    'Printing and first-class postage included',
    'Every occasion, unlimited custom',
    '30-day card preview window',
    'Send a card now, no occasion needed',
    'Relationship Wrapped, yearly recap',
    'SMS, email + push notifications',
  ],
};

export default function PricingCard({
  tier,
  name,
  monthlyUsd,
  annualUsd,
  priceMonthly,
  priceAnnual,
  annual,
  popular = false,
}: PricingCardProps) {
  const [loading, setLoading] = useState(false);
  const [error,   setError  ] = useState('');
  const navigate  = useNavigate();
  const { user, profile } = useAuth();

  const displayPrice  = annual ? annualUsd   : monthlyUsd;
  const period        = annual ? '/year'     : '/month';
  const monthlyEquiv  = annual ? (annualUsd / 12).toFixed(2) : null;
  const priceId       = annual ? priceAnnual : priceMonthly;

  async function handleStartTrial() {
    setError('');

    // Not logged in — send to signup, return here after
    if (!user) {
      navigate('/signup?next=/pricing');
      return;
    }

    if (!priceId) {
      setError('Price not configured, contact support.');
      return;
    }

    setLoading(true);
    try {
      // Past-due users must resolve their failed payment before changing
      // plans. Stripe will reject the change otherwise; routing them to
      // the billing portal lets them update their card and recover.
      if (profile?.subscription_status === 'past_due') {
        const json = await apiFetch<{ url: string }>('/api/stripe/create-portal-session', {
          method: 'POST',
          body: JSON.stringify({ returnPath: '/dashboard?subscribed=1' }),
        });
        if (json.url) window.location.href = json.url;
        return;
      }

      // Canceled/no subscription → Stripe Checkout (collects card)
      if (!profile?.subscription_status || profile.subscription_status === 'canceled') {
        const json = await apiFetch<{ url: string }>('/api/stripe/create-checkout-session', {
          method: 'POST',
          body: JSON.stringify({ priceId, returnPath: '/dashboard?subscribed=1' }),
        });
        if (json.url) window.location.href = json.url;
        return;
      }

      // New user, no prior subscription — start free trial
      await apiFetch('/api/stripe/start-trial', {
        method: 'POST',
        body: JSON.stringify({ priceId }),
      });
      navigate('/dashboard?trial_started=1');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  async function handleManageBilling() {
    setLoading(true);
    try {
      const json = await apiFetch<{ url: string }>('/api/stripe/create-portal-session', {
        method: 'POST',
        body: JSON.stringify({ returnPath: '/pricing' }),
      });
      if (json.url) window.location.href = json.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  const features = FEATURES[tier] ?? [];

  const isCurrentPlan =
    !!user &&
    profile?.tier === tier &&
    (profile?.subscription_status === 'active' || profile?.subscription_status === 'trialing');

  const isActiveSubscriber =
    !!user &&
    (profile?.subscription_status === 'active' || profile?.subscription_status === 'trialing');

  return (
    <div
      className={`relative flex flex-col rounded-[20px] p-8 transition-shadow ${
        popular
          ? 'bg-navy text-white shadow-warm-xl ring-2 ring-sage scale-[1.02]'
          : 'bg-white text-navy shadow-warm hover:shadow-warm-lg'
      }`}
    >
      {/* Most Popular badge */}
      {popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-sage px-4 py-1 text-xs font-semibold font-body text-navy shadow-sm tracking-wide uppercase">
            Most Popular
          </span>
        </div>
      )}

      {/* Tier name */}
      <h3 className={`font-heading text-xl font-semibold mb-1 ${popular ? 'text-white' : 'text-navy'}`}>
        {name}
      </h3>

      {/* Who it's for */}
      <p className={`font-accent italic text-sm ${popular ? 'text-sage/90' : 'text-sage'}`}>
        {AUDIENCE[tier] ?? ''}
      </p>

      {/* Price */}
      <div className="mt-4 mb-1 flex items-end gap-1">
        <span className={`font-heading text-4xl font-bold ${popular ? 'text-white' : 'text-navy'}`}>
          ${displayPrice.toFixed(2)}
        </span>
        <span className={`font-body text-sm mb-1.5 ${popular ? 'text-white/70' : 'text-gray-500'}`}>
          {period}
        </span>
      </div>

      {/* Monthly equivalent (annual view only) */}
      {monthlyEquiv && (
        <p className={`font-body text-xs mb-1 ${popular ? 'text-white/60' : 'text-gray-400'}`}>
          ${monthlyEquiv} / mo when billed annually
        </p>
      )}

      {/* Free trial line */}
      <p className={`font-accent italic text-sm mt-1 mb-6 ${popular ? 'text-sage/90' : 'text-sage'}`}>
        14-day free trial, no card required
      </p>

      {/* CTA */}
      {isCurrentPlan ? (
        <button
          type="button"
          onClick={handleManageBilling}
          disabled={loading}
          className={`w-full rounded-xl py-3 px-6 font-body text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed ${
            popular
              ? 'bg-white/20 text-white hover:bg-white/30 focus-visible:ring-white'
              : 'bg-gray-100 text-navy hover:bg-gray-200 focus-visible:ring-navy'
          }`}
        >
          {loading ? 'Loading…' : '✓ Current plan, manage billing'}
        </button>
      ) : isActiveSubscriber ? (
        <button
          type="button"
          onClick={handleManageBilling}
          disabled={loading}
          className={`w-full rounded-xl py-3 px-6 font-body text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed ${
            popular
              ? 'bg-sage text-navy hover:bg-sage/90 focus-visible:ring-sage'
              : 'bg-navy text-white hover:bg-navy/90 focus-visible:ring-navy'
          }`}
        >
          {loading ? 'Loading…' : 'Switch to this plan'}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleStartTrial}
          disabled={loading}
          className={`w-full rounded-xl py-3 px-6 font-body text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed ${
            popular
              ? 'bg-sage text-navy hover:bg-sage/90 focus-visible:ring-sage'
              : 'bg-navy text-white hover:bg-navy/90 focus-visible:ring-navy'
          }`}
        >
          {loading ? 'Starting…' : 'Start free, no card needed'}
        </button>
      )}

      {error && (
        <p className="mt-2 text-center text-xs text-rose font-body">{error}</p>
      )}

      {/* Divider */}
      <div className={`my-6 border-t ${popular ? 'border-white/20' : 'border-gray-100'}`} />

      {/* Features */}
      <ul className="flex flex-col gap-3">
        {features.map((feat) => (
          <li key={feat} className="flex items-start gap-2.5">
            <svg
              className={`mt-0.5 h-4 w-4 flex-shrink-0 ${popular ? 'text-sage' : 'text-sage'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span className={`font-body text-sm leading-snug ${popular ? 'text-white/85' : 'text-gray-600'}`}>
              {feat}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
